/**
 * MCP server configuration — the list of Model Context Protocol servers this
 * CLI can launch locally and negotiate with. Stored in `~/.standardagents/mcp.json`
 * so it persists across sessions and machines (per-user, not per-thread).
 *
 * Each server is a local process the CLI spawns and speaks JSON-RPC 2.0 to over
 * stdio (see `mcp.ts`). The CLI is the MCP *host*: it owns the connection, does
 * the capability handshake, and forwards the agent's tool calls to the server.
 */
import fs from "node:fs";
import path from "node:path";
import os from "node:os";

/**
 * Resolve the config file path at call time (not module load) so it honors an
 * override. `STANDARDAGENTS_MCP_CONFIG` points at an explicit mcp.json — tests
 * set it to a throwaway temp file so they never read or clobber the user's real
 * `~/.standardagents/mcp.json`.
 */
function configFile(): string {
  return process.env.STANDARDAGENTS_MCP_CONFIG || path.join(os.homedir(), ".standardagents", "mcp.json");
}

/** A locally-launchable MCP server (stdio transport). */
export interface McpServerConfig {
  /** Stable, human-readable id (used to namespace tools, e.g. `mcp:<name>/<tool>`). */
  name: string;
  /** Executable to spawn (e.g. `node`, `npx`, `uvx`, an absolute path). */
  command: string;
  /** Arguments passed to the command. */
  args: string[];
  /** Extra environment variables for the server process (merged over the CLI's env). */
  env?: Record<string, string>;
  /** Working directory for the server process (defaults to the project dir). */
  cwd?: string;
  /** When false, the server is remembered but not connected on startup. */
  enabled: boolean;
}

export interface McpConfigFile {
  servers: Record<string, McpServerConfig>;
}

export function loadMcpConfig(): McpConfigFile {
  try {
    const raw = fs.readFileSync(configFile(), "utf8");
    const parsed = JSON.parse(raw) as McpConfigFile;
    if (!parsed.servers || typeof parsed.servers !== "object") parsed.servers = {};
    return parsed;
  } catch {
    return { servers: {} };
  }
}

export function listMcpServers(): McpServerConfig[] {
  const cfg = loadMcpConfig();
  return Object.values(cfg.servers).sort((a, b) => a.name.localeCompare(b.name));
}

export function saveMcpServer(server: McpServerConfig): void {
  const cfg = loadMcpConfig();
  cfg.servers[server.name] = server;
  write(cfg);
}

export function removeMcpServer(name: string): void {
  const cfg = loadMcpConfig();
  delete cfg.servers[name];
  write(cfg);
}

export function setMcpServerEnabled(name: string, enabled: boolean): void {
  const cfg = loadMcpConfig();
  const s = cfg.servers[name];
  if (!s) return;
  s.enabled = enabled;
  write(cfg);
}

function write(cfg: McpConfigFile): void {
  const file = configFile();
  fs.mkdirSync(path.dirname(file), { recursive: true });
  fs.writeFileSync(file, JSON.stringify(cfg, null, 2), { mode: 0o600 });
}

/**
 * Parse a server definition from a CLI-style command string, e.g.
 *   `weather: npx -y @modelcontextprotocol/server-weather`
 * The part before the first `:` is the server name; the rest is the command +
 * args. Returns null if the shape is unusable.
 */
export function parseServerSpec(spec: string): McpServerConfig | null {
  const trimmed = spec.trim();
  const colon = trimmed.indexOf(":");
  if (colon <= 0) return null;
  const name = trimmed.slice(0, colon).trim();
  const rest = trimmed.slice(colon + 1).trim();
  if (!name || !rest) return null;
  const parts = tokenize(rest);
  if (!parts.length) return null;
  const [command, ...args] = parts;
  return { name, command, args, enabled: true };
}

/**
 * Build a server config from a short name + a full command line, e.g.
 *   serverFromCommand("playwright", "npx -y @playwright/mcp@latest")
 * Returns null if the name or command is unusable.
 */
export function serverFromCommand(
  name: string,
  commandLine: string,
  env?: Record<string, string>
): McpServerConfig | null {
  const cleanName = name.trim();
  const parts = tokenize(commandLine.trim());
  if (!cleanName || !parts.length) return null;
  const [command, ...args] = parts;
  return { name: cleanName, command, args, env, enabled: true };
}

/** Minimal shell-ish tokenizer that honours single/double quotes (no expansion). */
function tokenize(input: string): string[] {
  const out: string[] = [];
  let cur = "";
  let quote: '"' | "'" | null = null;
  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (quote) {
      if (ch === quote) quote = null;
      else cur += ch;
    } else if (ch === '"' || ch === "'") {
      quote = ch;
    } else if (/\s/.test(ch)) {
      if (cur) {
        out.push(cur);
        cur = "";
      }
    } else {
      cur += ch;
    }
  }
  if (cur) out.push(cur);
  return out;
}
